/* =========================================================
   Customer portal — shared client
   ---------------------------------------------------------
   Supabase OTP auth + IEP customer-portal API helpers.
   Exposes window.MPPortal, used by:
     - members-lander.js   (the /pages/members login + credit page)
     - the cart-drawer credit badge (init block at the bottom of this file)

   Config comes from window.MP_PORTAL (set in layout/theme.liquid):
     { supabaseUrl, supabaseAnonKey, customerApiUrl }

   Supabase JS is loaded lazily from CDN only when actually needed — visitors
   with no stored session never pay for it.
   ========================================================= */

window.MPPortal = (function () {
  'use strict';

  var cfg = window.MP_PORTAL || {};
  var SUPABASE_CDN = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.min.js';
  var clientPromise = null;

  function apiBase() {
    return String(cfg.customerApiUrl || '').replace(/\/+$/, '');
  }

  // Derive Supabase's localStorage session key from the project URL so we can
  // cheaply detect a logged-in user without loading the (heavy) Supabase bundle.
  function projectRef() {
    var m = String(cfg.supabaseUrl || '').match(/^https?:\/\/([^.]+)\./);
    return m ? m[1] : '';
  }

  function hasStoredSession() {
    try {
      var ref = projectRef();
      if (!ref) return false;
      return !!window.localStorage.getItem('sb-' + ref + '-auth-token');
    } catch (e) {
      return false;
    }
  }

  function loadSupabaseScript() {
    return new Promise(function (resolve, reject) {
      if (window.supabase && window.supabase.createClient) return resolve();
      var existing = document.querySelector('script[data-supabase-umd]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Supabase failed to load')); });
        return;
      }
      var s = document.createElement('script');
      s.src = SUPABASE_CDN;
      s.async = true;
      s.setAttribute('data-supabase-umd', '');
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Supabase failed to load')); };
      document.head.appendChild(s);
    });
  }

  function getClient() {
    if (clientPromise) return clientPromise;
    clientPromise = loadSupabaseScript().then(function () {
      if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) {
        throw new Error('Customer portal is not configured (missing Supabase settings).');
      }
      return window.supabase.createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false
        }
      });
    });
    return clientPromise;
  }

  // --- Auth flow -------------------------------------------------------------

  // Step 1: email → ask our backend to generate + email a one-time code via
  // Resend. (Supabase still mints/verifies the code; this just controls delivery.)
  async function sendOtp(email) {
    var res = await fetch(apiBase() + '/api/customer-portal/send-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    });
    if (!res.ok) {
      var err = await res.json().catch(function () { return {}; });
      throw new Error(err.error || 'Could not send your code. Please try again.');
    }
    return true;
  }

  // Step 2: code → verify, then register/tag the customer in the IEP backend.
  async function verifyOtp(email, code) {
    var client = await getClient();
    // Returning customers verify with type 'email'; brand-new accounts created via
    // signInWithOtp can require type 'signup'. Try 'email' first, then fall back.
    var res = await client.auth.verifyOtp({ email: email, token: code, type: 'email' });
    if (res.error) {
      var signupRes = await client.auth.verifyOtp({ email: email, token: code, type: 'signup' });
      if (signupRes.error) throw res.error; // surface the original (clearer) error
      res = signupRes;
    }

    var token = res.data && res.data.session && res.data.session.access_token;
    if (!token) throw new Error('No session was returned.');

    var authRes = await fetch(apiBase() + '/api/customer-portal/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: token })
    });

    if (!authRes.ok) {
      // Backend refused (e.g. an admin-domain email). Don't leave a half-signed-in
      // state — tear the Supabase session back down.
      var err = await authRes.json().catch(function () { return {}; });
      await signOut();
      throw new Error(err.error || 'We could not verify your account.');
    }

    var profile = await authRes.json().catch(function () { return {}; });
    return profile.account || null;
  }

  async function getToken() {
    if (!hasStoredSession()) return null;
    var client = await getClient();
    var res = await client.auth.getSession();
    return (res.data && res.data.session && res.data.session.access_token) || null;
  }

  async function getEmail() {
    if (!hasStoredSession()) return null;
    var client = await getClient();
    var res = await client.auth.getSession();
    return (res.data && res.data.session && res.data.session.user && res.data.session.user.email) || null;
  }

  async function isSignedIn() {
    if (!hasStoredSession()) return false;
    try { return !!(await getToken()); } catch (e) { return false; }
  }

  async function signOut() {
    try {
      var client = await getClient();
      await client.auth.signOut();
    } catch (e) { /* best effort */ }
  }

  // --- Credit ----------------------------------------------------------------

  async function fetchCredit() {
    var token = await getToken();
    if (!token) return null;
    var res = await fetch(apiBase() + '/api/customer-portal/credit', {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!res.ok) return null;
    return res.json(); // { available, currency, credits: [...] }
  }

  async function fetchOrders() {
    var token = await getToken();
    if (!token) return null;
    var res = await fetch(apiBase() + '/api/customer-portal/orders', {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!res.ok) return null;
    return res.json(); // { first_name, orders: [...], address: {...}|null }
  }

  // Line items + tracking for one order — fetched lazily when a row is expanded.
  async function fetchOrderDetail(id) {
    var token = await getToken();
    if (!token) return null;
    var res = await fetch(apiBase() + '/api/customer-portal/orders?id=' + encodeURIComponent(id), {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!res.ok) return null;
    return res.json(); // { line_items, fulfillments, order_status_url }
  }

  // Mint a single-use discount code worth the current balance, to apply to the cart.
  async function redeemCredit() {
    var token = await getToken();
    if (!token) return null;
    var res = await fetch(apiBase() + '/api/customer-portal/redeem', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!res.ok) return null;
    return res.json(); // { code, amount, currency }
  }

  // Remove the applied store-credit discount from the cart/checkout session.
  // Best-effort: clear the discount cookie(s) and re-evaluate the cart. The
  // authoritative revocation is deleting the code server-side (below), after
  // which Shopify drops the now-unknown code on the next cart evaluation anyway.
  async function clearCartDiscount() {
    try {
      var expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = 'discount_code=; expires=' + expire + '; path=/';
      document.cookie = 'discount=; expires=' + expire + '; path=/';
    } catch (e) {}
    try {
      // A cart write forces Shopify to recalculate and drop the invalid discount.
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { mp_store_credit_available: '', mp_customer_email: '' } })
      });
    } catch (e) {}
    try { if (window.MindCart && window.MindCart.refresh) window.MindCart.refresh(); } catch (e) {}
  }

  // Revoke the customer's active store-credit code immediately (on logout) so a
  // logged-out guest can't use it. Must run BEFORE signOut (needs a valid token).
  // 'mp_credit_code_v2' must match CODE_KEY in the cart-apply block below.
  async function revokeCredit() {
    var code = null;
    try {
      var raw = JSON.parse(window.localStorage.getItem('mp_credit_code_v2') || 'null');
      code = raw && raw.code;
    } catch (e) {}
    try { window.localStorage.removeItem('mp_credit_code_v2'); } catch (e) {}

    // Delete the code server-side (authoritative expiry).
    if (code) {
      var token = await getToken();
      if (token) {
        try {
          await fetch(apiBase() + '/api/customer-portal/revoke', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: code })
          });
        } catch (e) { /* best effort */ }
      }
    }

    // Clear it from the cart session too, so it's gone immediately.
    await clearCartDiscount();
  }

  return {
    hasStoredSession: hasStoredSession,
    sendOtp: sendOtp,
    verifyOtp: verifyOtp,
    getToken: getToken,
    getEmail: getEmail,
    isSignedIn: isSignedIn,
    signOut: signOut,
    fetchCredit: fetchCredit,
    fetchOrders: fetchOrders,
    fetchOrderDetail: fetchOrderDetail,
    redeemCredit: redeemCredit,
    revokeCredit: revokeCredit
  };
})();

/* ---------------------------------------------------------
   Cart credit badge + auto-applied store-credit discount
   ---------------------------------------------------------
   When a signed-in customer has a balance and items in the cart, mint a
   single-use discount code = balance and apply it (works at guest checkout).
   The order webhook decrements the internal ledger by the amount actually used.
   --------------------------------------------------------- */
(function () {
  'use strict';

  var CODE_KEY = 'mp_credit_code_v2'; // bumped: v1 codes were non-combinable

  function fmtMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(amount || 0));
    } catch (e) {
      return (currency || 'USD') + ' ' + Number(amount || 0).toFixed(2);
    }
  }

  function renderBadge(data) {
    document.querySelectorAll('[data-cart-credit]').forEach(function (el) {
      var txt = el.querySelector('[data-cart-credit-text]');
      if (txt) {
        txt.textContent = fmtMoney(data.available, data.currency) + ' store credit — applied at checkout';
      }
      el.hidden = false;
    });
  }

  function getCachedCode() {
    try {
      var raw = JSON.parse(localStorage.getItem(CODE_KEY) || 'null');
      if (raw && raw.code && raw.exp && Date.now() < raw.exp) return raw.code;
    } catch (e) {}
    return null;
  }
  function cacheCode(code) {
    try { localStorage.setItem(CODE_KEY, JSON.stringify({ code: code, exp: Date.now() + 55 * 60 * 1000 })); } catch (e) {}
  }

  async function getCart() {
    try {
      return await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    } catch (e) { return null; }
  }

  // Mint (or reuse) the customer's discount code and apply it to the cart.
  // Idempotent: no-op when the cart is empty or our code is already applied.
  async function applyCreditToCart() {
    if (!window.MPPortal || !window.MPPortal.hasStoredSession()) return;

    var credit = window.__mpCredit || await window.MPPortal.fetchCredit();
    if (!credit || !(Number(credit.available) > 0)) return;

    var cart = await getCart();
    if (!cart || cart.item_count === 0) return;

    var already = (cart.discount_codes || []).some(function (d) {
      return /^MPC-/i.test(d.code) && d.applicable !== false;
    });
    if (already) return;

    var code = getCachedCode();
    if (!code) {
      var r = await window.MPPortal.redeemCredit();
      if (!r || !r.code) return;
      code = r.code;
      cacheCode(code);
    }

    // ?redirect=/cart.js applies the code and returns the updated cart JSON.
    try {
      await fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart.js'), {
        headers: { Accept: 'application/json' }
      });
    } catch (e) {}

    // The full cart page is server-rendered — reload once so Liquid shows the
    // discount row + reduced total. (Next run sees the code already applied and
    // skips, so this doesn't loop.) The drawer can re-render live instead.
    if (document.querySelector('[data-cart-page]')) {
      window.location.reload();
      return;
    }
    if (window.MindCart && window.MindCart.refresh) window.MindCart.refresh();
  }

  async function init() {
    var signedIn = window.MPPortal && window.MPPortal.hasStoredSession();

    // Make the header account control session-aware so a logged-in customer
    // goes straight to their dashboard instead of bouncing through the login page.
    if (signedIn) {
      document.querySelectorAll('[data-mp-account-link]').forEach(function (a) {
        a.setAttribute('href', '/pages/account');
      });
      document.querySelectorAll('[data-mp-account-label]').forEach(function (el) {
        el.textContent = 'Account';
      });
    }

    if (!signedIn) return;
    var data = await window.MPPortal.fetchCredit();
    if (!data || !(Number(data.available) > 0)) return;
    window.__mpCredit = data;
    renderBadge(data);
    applyCreditToCart();
  }

  // Re-apply after the cart changes (add-to-cart or opening the drawer).
  document.addEventListener('submit', function (e) {
    if (e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]')) {
      setTimeout(applyCreditToCart, 900);
    }
  });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-cart-open]')) {
      setTimeout(applyCreditToCart, 350);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
